import React, { useState } from 'react';
import { ref, push } from 'firebase/database';
import { database, auth } from '../../firebaseConfig';

const GOOGLE_REVIEW_LINKS = {
  'san-juan': 'https://g.page/r/CVmOb3BkuMKREBM/review',
  'chicago': 'https://g.page/r/CQUoZL--UOsMEBM/review',
  'mexico-city': 'https://g.page/r/CQnKBOrpdXGfEBM/review',
};

/**
 * Feedback Survey Component
 * Collects star rating and optional text feedback after mission completion
 */
export function FeedbackSurvey({ tourSlug, onComplete }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a star rating');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const user = auth.currentUser;
      const feedbackData = {
        tourSlug,
        rating,
        feedback: feedback.trim(),
        userId: user?.uid || 'anonymous',
        userEmail: user?.email || 'anonymous',
        timestamp: Date.now(),
        createdAt: new Date().toISOString()
      };

      // Save to Firebase Realtime Database
      const feedbackRef = ref(database, `feedback/${tourSlug}`);
      await push(feedbackRef, feedbackData);

      // Call completion callback (badge flag is set in parent component)
      if (onComplete) onComplete();
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <div
      className="min-h-screen bg-slate-900 flex items-center justify-center p-6"
      style={{
        paddingTop: 'max(1.5rem, calc(env(safe-area-inset-top, 0px) + 1.5rem))',
        paddingBottom: 'max(1.5rem, calc(env(safe-area-inset-bottom, 0px) + 1.5rem))'
      }}
    >
      <style>{`
        .star-button {
          transition: all 0.2s ease;
        }
        .star-button:hover {
          transform: scale(1.1);
        }
        .star-button:active {
          transform: scale(0.95);
        }
        .fade-in {
          animation: fadeIn 0.5s ease-out;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>

      <div className="w-full max-w-lg fade-in">
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
            style={{
              background: 'rgba(16, 185, 129, 0.2)',
              border: '2px solid rgba(16, 185, 129, 0.5)'
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#10B981"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
            </svg>
          </div>

          <h2 className="text-2xl font-bold text-white mb-2 font-mono">
            MISSION FEEDBACK
          </h2>
          <p className="text-sm text-gray-400 font-mono">
            Help us improve future operations
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-xl p-6"
          style={{
            background: '#1e293b',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }}
        >
          {/* Star Rating */}
          <div className="mb-6">
            <label className="block text-sm font-mono font-bold text-emerald-400 mb-3 uppercase tracking-wider">
              Rate Your Experience
            </label>
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="star-button"
                  disabled={isSubmitting}
                  aria-label={`${star} star${star > 1 ? 's' : ''}`}
                >
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 24 24"
                    fill={star <= displayRating ? '#10B981' : 'none'}
                    stroke={star <= displayRating ? '#10B981' : '#64748b'}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      filter: star <= displayRating ? 'drop-shadow(0 0 6px rgba(16, 185, 129, 0.4))' : 'none'
                    }}
                  >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                  </svg>
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-center mt-2 text-sm font-mono text-emerald-400">
                {rating === 5 && 'Outstanding! 🎉'}
                {rating === 4 && 'Great! 👍'}
                {rating === 3 && 'Good 👌'}
                {rating === 2 && 'Could be better'}
                {rating === 1 && 'Needs improvement'}
              </p>
            )}
          </div>

          {/* For 5 stars: Google Review CTA */}
          {rating === 5 && (
            <div className="mb-6">
              <div
                className="p-4 rounded-lg text-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.1) 100%)',
                  border: '2px solid rgba(16, 185, 129, 0.5)'
                }}
              >
                <div className="text-3xl mb-2">🌟</div>
                <h3 className="text-lg font-bold text-white font-mono mb-2">
                  LOVED THE MISSION?
                </h3>
                <p className="text-sm text-gray-300 font-mono mb-4">
                  Help others discover this adventure by leaving a review on Google
                </p>
                <a
                  href={GOOGLE_REVIEW_LINKS[tourSlug] || GOOGLE_REVIEW_LINKS['san-juan']}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full font-semibold uppercase tracking-wide text-sm font-mono transition-all hover:scale-105"
                  style={{
                    padding: '12px 16px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#ffffff',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 0 12px rgba(16, 185, 129, 0.3)'
                  }}
                  onClick={() => {
                    // Track 5-star rating in Firebase before redirecting
                    const feedbackRef = ref(database, `feedback/${tourSlug}`);
                    const feedbackData = {
                      tourSlug,
                      rating,
                      feedback: "Redirected to Google Review",
                      userId: auth.currentUser?.uid || 'anonymous',
                      userEmail: auth.currentUser?.email || 'anonymous',
                      timestamp: Date.now(),
                      createdAt: new Date().toISOString(),
                      redirectedToGoogle: true
                    };
                    push(feedbackRef, feedbackData).catch(err => console.error("Failed to track feedback:", err));
                  }}
                >
                  ⭐ Leave a Google Review
                </a>
                <p className="text-xs mt-3 text-gray-400 font-mono">
                  Opens in a new tab
                </p>
              </div>
            </div>
          )}

          {/* For 1-4 stars: Feedback Text */}
          {rating > 0 && rating < 5 && (
            <div className="mb-6">
              <label
                htmlFor="feedback-text"
                className="block text-sm font-mono font-bold text-emerald-400 mb-3 uppercase tracking-wider"
              >
                What Could We Improve? (Optional)
              </label>
              <textarea
                id="feedback-text"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                disabled={isSubmitting}
                placeholder="Your feedback helps us improve future missions..."
                rows={4}
                className="w-full px-4 py-3 rounded-lg font-mono text-sm resize-none"
                style={{
                  background: 'rgba(15, 23, 42, 0.5)',
                  border: '1px solid rgba(71, 85, 105, 0.5)',
                  color: '#ffffff',
                  outline: 'none'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = 'rgba(16, 185, 129, 0.5)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(71, 85, 105, 0.5)';
                }}
              />
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div
              className="mb-4 p-3 rounded-lg text-sm font-mono"
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#EF4444'
              }}
            >
              {error}
            </div>
          )}

          {/* Submit Button - Only for 1-4 star ratings */}
          {rating > 0 && rating < 5 && (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full py-3 rounded-lg font-semibold uppercase tracking-wide text-sm font-mono transition-all"
              style={{
                background: isSubmitting
                  ? 'rgba(71, 85, 105, 0.5)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1
              }}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" opacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" opacity="0.75" />
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit Feedback'
              )}
            </button>
          )}

          {/* Skip/Continue Button for 5-star ratings */}
          {rating === 5 && (
            <button
              onClick={() => onComplete && onComplete()}
              disabled={isSubmitting}
              className="w-full py-3 rounded-lg font-medium text-sm font-mono transition-all"
              style={{
                background: 'rgba(71, 85, 105, 0.3)',
                color: '#9CA3AF',
                border: '1px solid rgba(71, 85, 105, 0.5)',
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              Continue (Already reviewed on Google)
            </button>
          )}

          {/* Skip Button for ratings not yet selected or 1-4 stars */}
          {rating < 5 && (
            <button
              onClick={() => onComplete && onComplete()}
              disabled={isSubmitting}
              className="w-full mt-3 py-2 rounded-lg font-medium text-sm font-mono transition-all"
              style={{
                background: 'transparent',
                color: '#9CA3AF',
                border: 'none',
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              Skip
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
